// frontend/src/pages/Promotions.jsx
// Full page for managing promotions: list view + timeline view. The list is
// built like the purchase order list: status buttons with counts, the name as
// the record link, rows that open into their items, and the row's next step
// plus a ⋯ menu.

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Tooltip } from '@mui/material';
import PageBanner from '../components/common/PageBanner';
import { AgGridReact } from 'ag-grid-react';
import { ICON, CalendarBlank, ListBullets, Plus } from '../icons';
import { useAuth, useIsShopify } from '../contexts/AuthContext';
import { useFormatters } from '../hooks/useFormatters';
import { usePromotions, useDeletePromotion, useApplyPromotion } from '../hooks/usePromotions';
import { translateRequestError } from '../queries/api';
import { ApiError } from '../utils/parseApiError';
import { useAgGridTheme } from '../utils/agGridTheme';
import { defaultColDef, defaultGridProps, copyOnlyContextMenuItems, actionColumn, flexColumn, everyRowIsMaster } from '../utils/gridDefaults';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useGridState } from '../hooks/useGridState';
import GridExportButton from '../components/common/GridExportButton';
import FilterChips from '../components/common/FilterChips';
import ExpandAllButton from '../components/grid/ExpandAllButton';
import { useExpandAllRows } from '../hooks/useExpandAllRows';
import FilterRow from '../components/common/FilterRow';
import SearchField from '../components/common/SearchField';
import { useGridPagination } from '../hooks/useGridPagination';
import { PromotionStatusCell } from '../components/grid/StatusChipCells';
import { derivePromotionStatus } from '../components/grid/statusDerivers';
import { promotionEffectiveStatus } from '../utils/promotionStatus';
import { useStoreToday } from '../hooks/useStoreToday';
import PromotionForm from '../components/promotions/PromotionForm';
import PromotionTimeline from '../components/promotions/PromotionTimeline';
import PromotionMembers from '../components/promotions/PromotionMembers';
import PromotionRowActions from '../components/promotions/PromotionRowActions';
import { PROMOTION_ACTION } from '../components/promotions/promotionActions';
import { promotionDeleteCopy } from '../components/promotions/promotionDeleteCopy';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Tile from '../components/layout/Tile';
import '../components/promotions/Promotions.css';
import '../styles/pages.css';
import { formatLiftValue } from '../utils/promotionLift';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

// The status buttons, in the order they read. 'all' is every promotion.
const STATUS_FILTERS = ['all', 'active', 'scheduled', 'draft', 'completed'];

// A save or an apply whose price push left items at their regular price
// (`not_started`, absent when every item went on sale): one toast that says
// so — the promotion's item list says why for each. With none on sale the
// promotion came back a draft.
function notStartedWarning(saved, t) {
  if (!saved.not_started) return null;
  return saved.status === 'draft'
    ? t('promotions.notStartedNone')
    : t('promotions.notStartedSome', { count: saved.not_started.length });
}

const Promotions = () => {
  const { t } = useTranslation();
  const { userContext, updatePreference } = useAuth();
  const isShopify = useIsShopify();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber } = useFormatters();
  // Anchors "is this promotion over?" on the store's calendar, not the
  // viewer's and not UTC. '' until the store resolves, which leaves the
  // stored status standing rather than guessing.
  const storeToday = useStoreToday();
  const [statusFilter, setStatusFilter] = useState(
    () => userContext?.user?.ui_preferences?.promotions_status_filter || 'all'
  );
  const { promotions, loading, error } = usePromotions();
  const deleteMutation = useDeletePromotion();
  const applyMutation = useApplyPromotion();
  const { mutateAsync: applyPromotion } = applyMutation;
  const applyingId = applyMutation.isPending ? applyMutation.variables.id : null;
  const [view, setView] = useState(
    () => userContext?.user?.ui_preferences?.promotions_view_mode || 'list'
  );
  const [formOpen, setFormOpen] = useState(false);
  const [editPromotion, setEditPromotion] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const deleteCopy = confirmDelete && promotionDeleteCopy(confirmDelete, isShopify, t);
  const [confirmEditActive, setConfirmEditActive] = useState(null);
  // Blast-radius confirm for applying a large price promo from the list:
  // { promotion, count } — set when apply returns 409 price_confirmation_required.
  const [confirmApply, setConfirmApply] = useState(null);
  const [searchText, setSearchText] = useState('');
  const pagination = useGridPagination(20);
  const getRowId = useCallback((params) => String(params.data.PromotionID), []);
  const gridState = useGridState('promotions_list');
  const expandAll = useExpandAllRows();

  // Filtered here, on the status each row's chip shows, so a button's count,
  // its rows and their chips agree — the stored status lags a promotion whose
  // end date has passed until the nightly job catches up.
  const statusOf = useCallback((promo) => promotionEffectiveStatus(promo, storeToday), [storeToday]);
  const visiblePromotions = useMemo(
    () => (promotions && statusFilter !== 'all'
      ? promotions.filter((promo) => statusOf(promo) === statusFilter)
      : promotions),
    [promotions, statusFilter, statusOf],
  );
  const statusOptions = useMemo(() => STATUS_FILTERS.map((value) => ({
    value,
    label: value === 'all' ? t('common.all') : t(`promotions.status_${value}`),
    count: promotions
      ? promotions.filter((promo) => value === 'all' || statusOf(promo) === value).length
      : undefined,
  })), [promotions, statusOf, t]);
  const pressStatus = (value) => {
    setStatusFilter(value);
    updatePreference('promotions_status_filter', value);
  };

  // Full-dataset export. Only the status filter maps server-side (the search
  // box is a client-side quick filter); scope 'all' drops it.
  const exportProps = {
    endpoint: '/api/promotions/export/',
    filename: 'promotions',
    getParams: useCallback(
      (scope) => (scope !== 'all' && statusFilter !== 'all'
        ? `&status=${encodeURIComponent(statusFilter)}`
        : ''),
      [statusFilter],
    ),
  };


  const handleCreate = () => {
    setEditPromotion(null);
    setFormOpen(true);
  };

  const handleEdit = useCallback((promotion) => {
    if (isShopify && promotion.status === 'active') {
      setConfirmEditActive(promotion);
      return;
    }
    setEditPromotion(promotion);
    setFormOpen(true);
  }, [isShopify]);

  const handleConfirmEditActive = () => {
    if (!confirmEditActive) return;
    setEditPromotion(confirmEditActive);
    setFormOpen(true);
    setConfirmEditActive(null);
  };

  // PromotionForm calls onSaved after a successful create/update; the
  // mutation it ran already invalidated the list, so this hook only
  // needs to say when items did not go on sale (no manual refetch).
  const handleFormSaved = (saved) => {
    const warning = notStartedWarning(saved, t);
    if (warning) showToast(warning, 'warning');
  };

  const handleDelete = async () => {
    if (!confirmDelete || deleteMutation.isPending) return;
    try {
      await deleteMutation.mutateAsync(confirmDelete.PromotionID);
      showToast(t('promotions.deleted'));
    } catch (err) {
      console.error('deletePromotion:', err.message);
      showToast(translateRequestError(err, t, 'promotions.deleteFailed'), 'error');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleApply = useCallback(async (promotion, { confirmed = false } = {}) => {
    try {
      const applied = await applyPromotion({ id: promotion.PromotionID, confirmed });
      setConfirmApply(null);
      const warning = notStartedWarning(applied, t);
      showToast(warning ?? t('promotions.applied'), warning ? 'warning' : undefined);
    } catch (err) {
      if (err instanceof ApiError && err.body?.error === 'price_confirmation_required') {
        // Large price promo — surface the blast radius; on confirm we re-apply
        // with confirmed:true. The push is async, so confirming returns fast.
        setConfirmApply({ promotion, count: err.body.member_item_count ?? null });
        return;
      }
      console.error('applyPromotion:', err.message);
      showToast(translateRequestError(err, t, 'promotions.applyFailed'), 'error');
    }
  }, [applyPromotion, t]);

  const runAction = useCallback((promotion, action) => {
    const run = {
      [PROMOTION_ACTION.APPLY]: handleApply,
      [PROMOTION_ACTION.EDIT]: handleEdit,
      [PROMOTION_ACTION.DELETE]: setConfirmDelete,
    };
    run[action](promotion);
  }, [handleApply, handleEdit]);

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: t('promotions.name'),
      ...flexColumn(),
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        // The name is the record link: it opens the promotion.
        innerRenderer: (p) => (
          <button
            type="button"
            className="link-button"
            onClick={(e) => { e.stopPropagation(); handleEdit(p.data); }}
          >
            {p.value}
          </button>
        ),
      },
    },
    {
      field: 'start_date',
      headerName: t('promotions.startDate'),
      type: 'dateColumn',
    },
    {
      field: 'end_date',
      headerName: t('promotions.endDate'),
      type: 'dateColumn',
    },
    {
      colId: 'effectiveStatus',
      headerName: t('common.status'),
      type: 'centeredColumn',
      // derivePromotionStatus collapses (status + end_date + today) into one of
      // draft/scheduled/active/completed for the chip + filter UI. `today` is
      // the STORE's, so a promotion reads finished when it is finished where
      // the store is — not when UTC says so, which for a store east of it
      // marked the last day of a running promotion 'completed'.
      valueGetter: (params) => derivePromotionStatus(params, storeToday),
      cellRenderer: PromotionStatusCell,
    },
    {
      field: 'items_count',
      headerName: t('promotions.itemsAffected'),
      type: 'numberColumn',
    },
    {
      headerName: t('promotions.lift'),
      type: 'centeredColumn',
      valueGetter: (params) => {
        const d = params.data;
        if (!d) return '';
        const { signed } = formatLiftValue(formatNumber, d.lift_type, d.lift_value);
        return d.lift_type === 'percentage' ? `${signed}%` : `${signed} ${t('promotions.units')}`;
      },
    },
    actionColumn({
      headerName: t('promotions.columnActions'),
      cellRenderer: (params) => (
        <PromotionRowActions
          promotion={params.data}
          onAction={runAction}
          applying={applyingId === params.data.PromotionID}
        />
      ),
    }),
  ], [t, formatNumber, storeToday, handleEdit, runAction, applyingId]);

  const toolbar = (
    <>
      <FilterChips options={statusOptions} value={statusFilter} onChange={pressStatus} />

      <Tooltip title={t('promotions.listView')}>
        <IconButton size="small" color={view === 'list' ? 'primary' : 'default'} onClick={() => { setView('list'); updatePreference('promotions_view_mode', 'list'); }} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
          <ListBullets size={ICON.lg} />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('promotions.timelineView')}>
        <IconButton size="small" color={view === 'timeline' ? 'primary' : 'default'} onClick={() => { setView('timeline'); updatePreference('promotions_view_mode', 'timeline'); }} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
          <CalendarBlank size={ICON.lg} />
        </IconButton>
      </Tooltip>
      <Button variant="contained" startIcon={<Plus size={ICON.md} />} onClick={handleCreate}>
        {t('promotions.createPromotion')}
      </Button>
    </>
  );

  return (
    <div className="page-container">
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.promotions')}
          toolbar={toolbar}
          headerActions={<GridExportButton {...exportProps} />}
        >
          <GridPage>
            <PageBanner severity="error" messages={[error]} />

            <FilterRow>
              <SearchField
                className="filter-row-search"
                label={t("promotions.searchLabel")}
                placeholder={t("promotions.searchPlaceholder")}
                value={searchText}
                onChange={setSearchText}
              />
            </FilterRow>
            {view === 'list' && <ExpandAllButton {...expandAll} />}

            <GridViewport>
              {view === 'list' && gridTheme && !error && (
                <GridEmptyState
                  title={t('promotions.noPromotions')}
                  // searchText drives AG Grid's quickFilterText; statusFilter is external.
                  filtersActive={!!searchText || statusFilter !== 'all'}
                >
                  <AgGridReact
                    {...defaultGridProps}
                    theme={gridTheme}
                    columnDefs={columnDefs}
                    rowData={visiblePromotions}
                    defaultColDef={defaultColDef}
                    columnTypes={columnTypes}
                    getRowId={getRowId}
                    {...gridState}
                    getContextMenuItems={copyOnlyContextMenuItems}
                    quickFilterText={searchText}
                    loading={loading}
                    {...pagination}
                    masterDetail={true}
                    detailCellRenderer={PromotionMembers}
                    detailRowAutoHeight={true}
                    isRowMaster={everyRowIsMaster}
                    {...expandAll.gridProps}
                  />
                </GridEmptyState>
              )}

              {view === 'timeline' && visiblePromotions && (
                <PromotionTimeline promotions={visiblePromotions} onEdit={handleEdit} />
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>

      {/* Create/Edit form */}
      {formOpen && (
        <PromotionForm
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditPromotion(null); }}
          onSaved={handleFormSaved}
          editPromotion={editPromotion}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        loading={deleteMutation.isPending}
        title={deleteCopy?.title}
        message={deleteCopy?.message}
        variant="danger"
        confirmLabel={t('btn.delete')}
        cancelLabel={t('common.cancel')}
      />

      {/* Edit active promotion confirmation (Shopify only) */}
      <ConfirmDialog
        open={!!confirmEditActive}
        onCancel={() => setConfirmEditActive(null)}
        onConfirm={handleConfirmEditActive}
        title={t('promotions.editActiveTitle')}
        message={t('promotions.editActiveMessage', { name: confirmEditActive?.name, count: confirmEditActive?.items_count })}
        variant="warning"
        confirmLabel={t('btn.edit')}
        cancelLabel={t('common.cancel')}
      />

      {/* Apply blast-radius confirmation (large price promo) */}
      <ConfirmDialog
        open={!!confirmApply}
        onCancel={() => setConfirmApply(null)}
        onConfirm={() => confirmApply && handleApply(confirmApply.promotion, { confirmed: true })}
        loading={applyMutation.isPending}
        title={t('promotions.confirmPriceTitle')}
        message={
          confirmApply?.count != null
            ? t('promotions.confirmPriceBody', { count: confirmApply.count })
            : t('promotions.confirmPriceBodyNoCount')
        }
        variant="warning"
        confirmLabel={t('promotions.confirmPriceConfirm')}
        cancelLabel={t('common.cancel')}
      />

    </div>
  );
};

export default Promotions;
